import { importProvidersFrom } from '@angular/core';
import { Routes } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import { AuthRedirectService } from './core/services/auth-redirect.service';
import { AccountsEffects } from './core/store/digital-footprint/accounts/accounts.effects';
import {
  ACCOUNTS_FEATURE_KEY,
  accountsReducer,
} from './core/store/digital-footprint/accounts/accounts.reducer';
import { AssetsEffects } from './core/store/digital-footprint/assets/assets.effects';
import {
  ASSETS_FEATURE_KEY,
  assetsReducer,
} from './core/store/digital-footprint/assets/assets.reducer';
import { VendorsEffects } from './core/store/digital-footprint/vendors/vendors.effects';
import {
  VENDORS_FEATURE_KEY,
  vendorsReducer,
} from './core/store/digital-footprint/vendors/vendors.reducer';
import { GeneralEffects } from './core/store/general/general.effects';
import {
  GENERAL_FEATURE_KEY,
  generalReducer,
} from './core/store/general/general.reducer';
import { IntegrationsEffects } from './core/store/integrations/integrations.effects';
import {
  INTEGRATIONS_FEATURE_KEY,
  integrationsReducer,
} from './core/store/integrations/integrations.reducer';
import { MyTasksEffects } from './core/store/mytasks/mytasks.effects';
import {
  MYTASKS_FEATURE_KEY,
  myTasksReducer,
} from './core/store/mytasks/mytasks.reducer';
import { pentestEffects } from './core/store/pentest/pentest.effects';
import {
  PENTEST_FEATURE_KEY,
  pentestReducer,
} from './core/store/pentest/pentest.reducer';
import { ReferenceEffects } from './core/store/reference/reference.effects';
import {
  REFERENCE_FEATURE_KEY,
  referenceReducer,
} from './core/store/reference/reference.reducer';
import { RisksEffects } from './core/store/risks/risks.effects';
import {
  RISKS_FEATURE_KEY,
  risksReducer,
} from './core/store/risks/risks.reducer';
import { ScanConfigurationEffects } from './core/store/scan-configuration/scan-configuration.effects';
import {
  SCAN_CONFIGURATION_FEATURE_KEY,
  scanConfigurationReducer,
} from './core/store/scan-configuration/scan-configuration.reducer';
import { SupportCenterEffects } from './core/store/support-center/support-center.effects';
import {
  SUPPORT_CENTER_FEATURE_KEY,
  supportCenterReducer,
} from './core/store/support-center/support-center.reducer';
import { TeamEffects } from './core/store/team/team.effects';
import { TEAM_FEATURE_KEY, teamReducer } from './core/store/team/team.reducer';
import { ThreatsEffects } from './core/store/threats/threats.effects';
import {
  THREATS_FEATURE_KEY,
  threatsReducer,
} from './core/store/threats/threats.reducer';
import { VulnerabilitiesEffects } from './core/store/vulnerabilities/vulnerabilities.effects';
import {
  VULNERABILITIES_FEATURE_KEY,
  vulnerabilitiesReducer,
} from './core/store/vulnerabilities/vulnerabilities.reducer';
import { NoAuthGuard } from './guards/prevent-logged-in-access-auth0.guard';

export const routes: Routes = [
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./modules/dashboard/security-dashboard.component').then(
        (m) => m.SecurityDashboardComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'security-posture',
    loadComponent: () =>
      import('./modules/security-posture/security-posture.component').then(
        (m) => m.SecurityPostureComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'compliance',
    loadComponent: () =>
      import('./modules/compliance/compliance.component').then(
        (m) => m.ComplianceComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'compliance/report',
    loadComponent: () =>
      import('./modules/compliance/report/compliance-report.component').then(
        (m) => m.ComplianceReportComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'exposure',
    canActivate: [AuthGuard],
    providers: [
      importProvidersFrom(
        StoreModule.forFeature(ASSETS_FEATURE_KEY, assetsReducer),
        EffectsModule.forFeature([AssetsEffects]),
      ),
      // The asset page's Tasks tab opens the shared EditTaskModal, which reads the
      // mytasks store — register it here too, as the risks route already does.
      importProvidersFrom(
        StoreModule.forFeature(MYTASKS_FEATURE_KEY, myTasksReducer),
        EffectsModule.forFeature([MyTasksEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(VENDORS_FEATURE_KEY, vendorsReducer),
        EffectsModule.forFeature([VendorsEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(ACCOUNTS_FEATURE_KEY, accountsReducer),
        EffectsModule.forFeature([AccountsEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(INTEGRATIONS_FEATURE_KEY, integrationsReducer),
        EffectsModule.forFeature([IntegrationsEffects]),
      ),
    ],
    children: [
      {
        path: 'assets-list',
        loadComponent: () =>
          import('./modules/digital-footprint/pages/assets-list/assets-list.component').then(
            (m) => m.AssetsListComponent,
          ),
      },
      {
        // Detail for one Domain/Subdomain. Sits under the list it is reached from, beside
        // vendors/:vendorId which it mirrors.
        path: 'assets-list/:assetId',
        loadComponent: () =>
          import('./modules/digital-footprint/pages/asset-overview/asset-overview.component').then(
            (m) => m.AssetOverviewComponent,
          ),
      },
      {
        path: 'assets',
        loadComponent: () =>
          import('./modules/digital-footprint/pages/assets-graph/assets-graph.component').then(
            (m) => m.AssetsGraphComponent,
          ),
      },
      {
        path: 'vendors',
        loadComponent: () =>
          import('./modules/digital-footprint/pages/vendors/vendors.component').then(
            (m) => m.VendorsComponent,
          ),
      },
      {
        path: 'vendors/:vendorId',
        loadComponent: () =>
          import('./modules/digital-footprint/pages/vendor-overview/vendor-overview.component').then(
            (m) => m.VendorOverviewComponent,
          ),
      },
      {
        path: 'accounts',
        loadComponent: () =>
          import('./modules/digital-footprint/pages/accounts/accounts.component').then(
            (m) => m.AccountsComponent,
          ),
      },
      {
        path: 'early-warnings',
        loadComponent: () =>
          import('./modules/early-warnings/early-warnings.component').then(
            (m) => m.EarlyWarningsComponent,
          ),
      },
      {
        path: '',
        redirectTo: 'assets-list',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'integrations',
    providers: [
      importProvidersFrom(
        StoreModule.forFeature(INTEGRATIONS_FEATURE_KEY, integrationsReducer),
        EffectsModule.forFeature([IntegrationsEffects]),
      ),
    ],
    loadComponent: () =>
      import('./modules/integrations/pages/integrations/integrations.component').then(
        (m) => m.IntegrationsComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    // OAuth redirect receiver — deliberately NOT behind AuthGuard so the guard's
    // own redirect can't race/drop the provider's code/tenant/state params.
    path: 'integrations/callback',
    loadComponent: () =>
      import('./modules/integrations/pages/integration-callback/integration-callback.component').then(
        (m) => m.IntegrationCallbackComponent,
      ),
  },
  {
    path: 'my-tasks',
    loadComponent: () =>
      import('./modules/my-tasks/pages/my-tasks/my-tasks.component').then(
        (m) => m.MainComponent,
      ),
    canActivate: [AuthGuard],
    providers: [
      importProvidersFrom(
        StoreModule.forFeature(MYTASKS_FEATURE_KEY, myTasksReducer),
        EffectsModule.forFeature([MyTasksEffects]),
      ),
    ],
  },
  {
    path: 'no-permission',
    loadComponent: () =>
      import('./modules/no-permission/no-permission.component').then(
        (m) => m.NoPermissionComponent,
      ),
    canActivate: [NoAuthGuard],
  },
  {
    // Deliberately unguarded: reached when the API refuses every route for this tenant,
    // so a guard that calls the API would be refused too and bounce the user in a loop.
    path: 'account-unavailable',
    loadComponent: () =>
      import(
        './modules/account-unavailable/account-unavailable.component'
      ).then((m) => m.AccountUnavailableComponent),
  },
  {
    path: 'notifications',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'all',
        loadComponent: () =>
          import('./modules/notifications/pages/all-notifications/all-notifications.component').then(
            (m) => m.AllNotificationsComponent,
          ),
      },
      {
        path: 'unread',
        loadComponent: () =>
          import('./modules/notifications/pages/unreaded-notifications/unreaded-notifications.component').then(
            (m) => m.UnreadedNotificationsComponent,
          ),
      },
      {
        path: '',
        redirectTo: 'all',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'onboarding',
    canActivate: [NoAuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/onboarding/components/onboarding/onboarding.component').then(
            (m) => m.OnboardingComponent,
          ),
      },
      {
        path: 'verify/:email',
        loadComponent: () =>
          import('./modules/onboarding/components/verify-email/verify-email.component').then(
            (m) => m.VerifyEmailComponent,
          ),
      },
      {
        path: 'complete/:token',
        loadComponent: () =>
          import('./modules/onboarding/components/onboarding-complete/onboarding-complete.component').then(
            (m) => m.OnboardingCompleteComponent,
          ),
      },
    ],
  },
  {
    path: 'pentesting',
    canActivate: [AuthGuard],
    providers: [
      importProvidersFrom(
        StoreModule.forFeature(PENTEST_FEATURE_KEY, pentestReducer),
        EffectsModule.forFeature([pentestEffects]),
      ),
    ],
    children: [
      {
        path: 'request',
        loadComponent: () =>
          import('./modules/pentesting/pages/request-pentest/request-pentest.component').then(
            (m) => m.RequestPentestComponent,
          ),
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./modules/pentesting/pages/pentest-reports/pentest-reports.component').then(
            (m) => m.PentestReportsComponent,
          ),
      },
      {
        path: '',
        redirectTo: 'request',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'pentesting-request',
    loadChildren: () =>
      import('./modules/public-pentest-request/public-pentest-request.component').then(
        (m) => m.PublicPentestRequestComponent,
      ),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'risks',
    canActivate: [AuthGuard],
    providers: [
      importProvidersFrom(
        StoreModule.forFeature(RISKS_FEATURE_KEY, risksReducer),
        EffectsModule.forFeature([RisksEffects]),
      ),
      // The risk-overview Tasks tab opens the shared EditTaskModal, which reads
      // the mytasks store — register it here too (not just on the my-tasks route).
      importProvidersFrom(
        StoreModule.forFeature(MYTASKS_FEATURE_KEY, myTasksReducer),
        EffectsModule.forFeature([MyTasksEffects]),
      ),
    ],
    children: [
      {
        path: 'register',
        loadComponent: () =>
          import('./modules/risks/pages/register/register.component').then(
            (m) => m.RegisterComponent,
          ),
      },
      {
        path: 'register/:riskId',
        loadComponent: () =>
          import('./modules/risks/pages/register/register-overview/register-overview.component').then(
            (m) => m.RegisterOverviewComponent,
          ),
      },
      {
        path: 'library',
        loadComponent: () =>
          import('./modules/risks/pages/library/library.component').then(
            (m) => m.LibraryComponent,
          ),
      },
      {
        path: '',
        redirectTo: 'register',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'settings',
    providers: [
      importProvidersFrom(
        StoreModule.forFeature(GENERAL_FEATURE_KEY, generalReducer),
        EffectsModule.forFeature([GeneralEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(REFERENCE_FEATURE_KEY, referenceReducer),
        EffectsModule.forFeature([ReferenceEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(
          SUPPORT_CENTER_FEATURE_KEY,
          supportCenterReducer,
        ),
        EffectsModule.forFeature([SupportCenterEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(TEAM_FEATURE_KEY, teamReducer),
        EffectsModule.forFeature([TeamEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(
          SCAN_CONFIGURATION_FEATURE_KEY,
          scanConfigurationReducer,
        ),
        EffectsModule.forFeature([ScanConfigurationEffects]),
      ),
    ],
    canActivate: [AuthGuard],
    children: [
      {
        path: 'general',
        loadComponent: () =>
          import('./modules/settings/pages/general/general.component').then(
            (m) => m.GeneralComponent,
          ),
      },
      {
        path: 'scan-configuration',
        loadComponent: () =>
          import('./modules/settings/pages/scan-configuration/scan-configuration.component').then(
            (m) => m.ScanConfigurationComponent,
          ),
      },
      {
        path: 'team-management',
        loadComponent: () =>
          import('./modules/settings/pages/team-management/team-management.component').then(
            (m) => m.TeamManagementComponent,
          ),
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./modules/settings/pages/support/support.component').then(
            (m) => m.SupportComponent,
          ),
      },
      {
        path: 'subscription',
        loadComponent: () =>
          import('./modules/settings/pages/plan/plan.component').then(
            (m) => m.PlanComponent,
          ),
      },
      {
        path: 'delete-account',
        loadComponent: () =>
          import(
            './modules/settings/pages/delete-account/delete-account.component'
          ).then((m) => m.DeleteAccountComponent),
      },
      {
        path: '',
        redirectTo: 'general',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'vulnerabilities',
    providers: [
      importProvidersFrom(
        StoreModule.forFeature(
          VULNERABILITIES_FEATURE_KEY,
          vulnerabilitiesReducer,
        ),
        EffectsModule.forFeature([VulnerabilitiesEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(THREATS_FEATURE_KEY, threatsReducer),
        EffectsModule.forFeature([ThreatsEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(RISKS_FEATURE_KEY, risksReducer),
        EffectsModule.forFeature([RisksEffects]),
      ),
      importProvidersFrom(
        StoreModule.forFeature(MYTASKS_FEATURE_KEY, myTasksReducer),
        EffectsModule.forFeature([MyTasksEffects]),
      ),
    ],
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/vulnerabilities/pages/vulnerabilities/vulnerabilities.component').then(
            (m) => m.VulnerabilitiesComponent,
          ),
      },
      {
        path: ':vulnerabilityId',
        loadComponent: () =>
          import('./modules/vulnerabilities/pages/vulnerabilities-overview/vulnerabilities-overview.component').then(
            (m) => m.VulnerabilitiesOverviewComponent,
          ),
      },
    ],
  },
  // Login route
  {
    path: 'login',
    loadComponent: () =>
      import('./modules/login/component/login.component').then(
        (m) => m.LoginComponent,
      ),
    canActivate: [NoAuthGuard],
  },
  // Default route
  {
    // Auth0 returns here after a refused sign-in was signed out
    // (AuthRedirectService.signOutAsAccessDenied); that tab goes to the
    // invitation-only screen once, everything else to the dashboard.
    path: '',
    redirectTo: () =>
      AuthRedirectService.consumeAccessDenied() ? 'no-permission' : 'dashboard',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];
